import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowUpRight, FileText, Plus, Save, Search } from "lucide-react";
import { toast } from "sonner";
import {
  request,
  type Bootstrap,
  type Obj,
  type Prompt,
  type Suite,
  label,
} from "./api";
import { Modal, Field, ErrorBox, Loading, CheckLine, Badge } from "./ui";

export function LibraryImport({
  onClose,
  onImported,
}: {
  onClose: () => void;
  onImported: (p: string, s: string, clock?: string, tz?: string) => void;
}) {
  const q = useQuery({
    queryKey: ["bench-native-library"],
    queryFn: () => request("/library"),
    retry: false,
  });
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Obj | null>(null);
  const [personas, setPersonas] = useState<string[]>([]);
  const [error, setError] = useState<unknown>();
  const [busy, setBusy] = useState(false);
  const choices = (q.data?.personas || []).filter(
    (p: Obj) =>
      p.cell_slug === selected?.cell_slug && p.language === selected?.language,
  );
  return (
    <Modal
      title="Scribe Library"
      description="Compare models with an existing prompt version and its personas."
      onClose={onClose}
      wide
    >
      <div className="library-shortcuts">
        {[
          { to: "/prompts", name: "Prompt Versions" },
          { to: "/personas", name: "Personas" },
          { to: "/knowledge", name: "Registers & Rules" },
          { to: "/runs", name: "Original Runs & Remediation" },
          { to: "/reviews", name: "Review Queue" },
        ].map((l) => (
          <Link key={l.to} to={l.to}>
            {l.name}
            <ArrowUpRight size={13} />
          </Link>
        ))}
      </div>
      <ErrorBox error={error || q.error} />
      {q.isLoading ? (
        <Loading />
      ) : (
        <div className="model-workbench">
          <aside className="profile-list">
            <div className="b-search">
              <Search size={15} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search the Scribe10 library"
              />
            </div>
            {(q.data?.prompts || [])
              .filter((p: Obj) =>
                (p.name + " " + p.cell_slug)
                  .toLowerCase()
                  .includes(search.toLowerCase()),
              )
              .map((p: Obj) => (
                <button
                  key={[p.cell_slug, p.language, p.name, p.version].join(":")}
                  className={
                    "profile-row " + (selected === p ? "selected" : "")
                  }
                  onClick={() => {
                    setSelected(p);
                    setPersonas([]);
                  }}
                >
                  <strong>{p.name}</strong>
                  <span>
                    {p.cell_slug} · {p.language} · v{p.version}
                  </span>
                  <small>
                    {p.body_length?.toLocaleString()} characters ·{" "}
                    {p.persona_count} personas
                  </small>
                </button>
              ))}
          </aside>
          <section className="profile-editor">
            <h3>{selected?.name || "Select Prompt"}</h3>
            <p className="b-muted">
              Select personas to include. Importing keeps the full source,
              rendered prompt, policy, and date.
            </p>
            {choices.map((p: Obj) => (
              <div className="persona-choice" key={p.id}>
                <CheckLine
                  checked={personas.includes(p.id)}
                  onChange={(v) =>
                    setPersonas(
                      v
                        ? [...personas, p.id]
                        : personas.filter((id) => id !== p.id),
                    )
                  }
                >
                  <strong>{p.name}</strong>
                </CheckLine>
                <p>{p.scenario}</p>
                <small>{p.expected_path}</small>
              </div>
            ))}
            {selected && (
              <button
                className="b-link"
                onClick={() => setPersonas(choices.map((p: Obj) => p.id))}
              >
                Select All {choices.length} Personas
              </button>
            )}
            <p className="b-alert">
              Imported scripts guide scenarios. They are not verified customer
              recordings or human-approved expected answers.
            </p>
            <button
              className="b-button primary"
              disabled={!selected || !personas.length || busy}
              onClick={async () => {
                setBusy(true);
                setError(null);
                try {
                  const r = await request("/library/import", {
                    cell_slug: selected?.cell_slug,
                    language: selected?.language,
                    prompt_name: selected?.name,
                    prompt_version: selected?.version,
                    persona_ids: personas,
                    clock_date: new Intl.DateTimeFormat("en-CA", {
                      timeZone: "Asia/Dubai",
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                    }).format(new Date()),
                  });
                  onImported(
                    r.prompt.id,
                    r.suite.id,
                    r.suite.clock_date,
                    r.suite.timezone,
                  );
                  toast.success("Prompt and personas imported");
                  onClose();
                } catch (e) {
                  setError(e);
                } finally {
                  setBusy(false);
                }
              }}
            >
              <FileText size={15} />
              Import Prompt & Personas
            </button>
          </section>
        </div>
      )}
    </Modal>
  );
}

export function PromptEditor({
  prompt,
  onClose,
  onSaved,
}: {
  prompt?: Prompt;
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const [form, setForm] = useState<Obj>(
    prompt
      ? { ...prompt }
      : {
          name: "",
          campaign: "",
          language: "en",
          content: "",
          rules: "",
          greeting: "",
          variables: {},
          source_status: "authored",
          policy_resolved: false,
        },
  );
  const [variables, setVariables] = useState(
    JSON.stringify(form.variables || {}, null, 2),
  );
  const [error, setError] = useState<unknown>();
  const [busy, setBusy] = useState(false);
  return (
    <Modal
      title={prompt ? "New Prompt Version" : "New Prompt"}
      description="Save a new version without changing previous comparisons."
      onClose={onClose}
      wide
    >
      <ErrorBox error={error} />
      <div className="b-grid three">
        <Field label="Prompt Name">
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>
        <Field label="Campaign">
          <input
            value={form.campaign}
            onChange={(e) => setForm({ ...form, campaign: e.target.value })}
          />
        </Field>
        <Field label="Language">
          <select
            value={form.language}
            onChange={(e) => setForm({ ...form, language: e.target.value })}
          >
            <option value="en">English</option>
            <option value="ar">Arabic</option>
            <option value="mixed">Mixed</option>
          </select>
        </Field>
      </div>
      <Field label="Complete System Prompt">
        <textarea
          dir="auto"
          className="prompt-editor-text"
          rows={18}
          value={form.content}
          onChange={(e) => setForm({ ...form, content: e.target.value })}
        />
      </Field>
      <div className="b-grid two">
        <Field label="Rules & Policy">
          <textarea
            dir="auto"
            rows={5}
            value={form.rules || ""}
            onChange={(e) => setForm({ ...form, rules: e.target.value })}
          />
        </Field>
        <Field label="Initial Greeting">
          <textarea
            dir="auto"
            rows={5}
            value={form.greeting || ""}
            onChange={(e) => setForm({ ...form, greeting: e.target.value })}
          />
        </Field>
      </div>
      <Field label="Shared Variables · JSON">
        <textarea
          className="mono"
          rows={3}
          value={variables}
          onChange={(e) => setVariables(e.target.value)}
        />
      </Field>
      <CheckLine
        checked={!!form.policy_resolved}
        onChange={(v) => setForm({ ...form, policy_resolved: v })}
      >
        I have reviewed this prompt’s policy interpretation
      </CheckLine>
      <div className="modal-actions">
        <button className="b-button" onClick={onClose}>
          Cancel
        </button>
        <button
          className="b-button primary"
          disabled={busy || !form.content || !form.name || !form.campaign}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              const p = await request<Prompt>(
                "/prompts" + (prompt ? "/" + prompt.id + "/versions" : ""),
                { ...form, variables: JSON.parse(variables) },
              );
              onSaved(p.id);
              onClose();
              toast.success("Prompt version saved");
            } catch (e) {
              setError(e);
            } finally {
              setBusy(false);
            }
          }}
        >
          <Save size={15} />
          Save {prompt ? "New Version" : "Prompt"}
        </button>
      </div>
    </Modal>
  );
}

export function ScenarioEditor({
  suite,
  prompt,
  data,
  onClose,
  onSaved,
}: {
  suite?: Suite;
  prompt?: Prompt;
  data: Bootstrap;
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const [form, setForm] = useState<Obj>(
    suite
      ? { ...suite }
      : {
          name: "",
          campaign: prompt?.campaign || "",
          language: prompt?.language || "en",
          cases: [],
        },
  );
  const [cases, setCases] = useState<Obj[]>(suite?.cases || []);
  const [index, setIndex] = useState(0);
  const [facts, setFacts] = useState(
    JSON.stringify(cases[0]?.facts || {}, null, 2),
  );
  const [history, setHistory] = useState(
    JSON.stringify(
      cases[0]?.checkpoints?.[0]?.history || cases[0]?.history || [],
      null,
      2,
    ),
  );
  const [error, setError] = useState<unknown>();
  const [busy, setBusy] = useState(false);
  const [brief, setBrief] = useState("");
  const [author, setAuthor] = useState(
    data.models.find((m) => !m.demonstration)?.id || "",
  );
  const c = cases[index];
  function patch(key: string, value: unknown) {
    setCases((cs) =>
      cs.map((x, i) => {
        if (i !== index) return x;
        const updated = {
          ...x,
          [key]: value,
          review_status: key === "review_status" ? value : "draft",
        };
        if (
          x.checkpoints?.length &&
          (key === "user_turns" || key === "history")
        )
          updated.checkpoints = x.checkpoints.map((cp: Obj, j: number) =>
            j === 0
              ? {
                  ...cp,
                  ...(key === "user_turns"
                    ? { user_turn: (value as string[])[0] }
                    : { history: value }),
                  reference_history_source: "Authored edit to frozen reference",
                }
              : cp,
          );
        return updated;
      }),
    );
  }
  function load(i: number) {
    setIndex(i);
    setFacts(JSON.stringify(cases[i]?.facts || {}, null, 2));
    setHistory(
      JSON.stringify(
        cases[i]?.checkpoints?.[0]?.history || cases[i]?.history || [],
        null,
        2,
      ),
    );
  }
  function add() {
    const next = {
      id: crypto.randomUUID(),
      source_group: crypto.randomUUID(),
      name: "New scenario",
      language: form.language,
      provenance: "authored",
      facts: {},
      history: [],
      user_turns: [""],
      expected_behavior: "",
      critical_rules: [],
      checks: [{ kind: "not_empty", value: true, severity: "major" }],
      review_status: "draft",
      persona: "",
    };
    setCases([...cases, next]);
    setIndex(cases.length);
    setFacts("{}");
    setHistory("[]");
  }
  return (
    <Modal
      title={suite ? "New Scenario Set Version" : "Create Scenarios"}
      description="Compare models with the same scenarios and fixed customer inputs."
      onClose={onClose}
      wide
    >
      <ErrorBox error={error} />
      <div className="b-grid three">
        <Field label="Scenario Set Name">
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>
        <Field label="Campaign">
          <input
            value={form.campaign}
            onChange={(e) => setForm({ ...form, campaign: e.target.value })}
          />
        </Field>
        <Field label="Language">
          <select
            value={form.language}
            onChange={(e) => setForm({ ...form, language: e.target.value })}
          >
            <option value="en">English</option>
            <option value="ar">Arabic</option>
            <option value="mixed">Mixed</option>
          </select>
        </Field>
      </div>
      <details className="author-assistant">
        <summary>Scenario Writing Assistant</summary>
        <p className="b-muted">
          Draft scenarios from the selected prompt, then review them before use.
        </p>
        <Field label="What Should These Scenarios Test?">
          <textarea
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            rows={3}
          />
        </Field>
        <div className="parameter-row">
          <select value={author} onChange={(e) => setAuthor(e.target.value)}>
            <option value="">Select Writing Model</option>
            {data.models.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
          <button
            className="b-button"
            disabled={busy || !author || !brief || !prompt}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                const r = await request("/author/brief", {
                  model_id: author,
                  brief,
                  prompt_id: prompt?.id,
                });
                setBrief(r.optimized);
                toast.success("Brief refined");
              } catch (e) {
                setError(e);
              } finally {
                setBusy(false);
              }
            }}
          >
            Refine Brief
          </button>
          <button
            className="b-button primary"
            disabled={busy || !author || !brief || !prompt}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                const r = await request("/author/scenarios", {
                  model_id: author,
                  brief,
                  prompt_id: prompt?.id,
                  count: 3,
                });
                setCases([...cases, ...r.cases]);
                toast.success("Three draft scenarios added");
              } catch (e) {
                setError(e);
              } finally {
                setBusy(false);
              }
            }}
          >
            Generate 3 Drafts
          </button>
        </div>
      </details>
      <div className="scenario-editor">
        <aside>
          <button className="b-button full" onClick={add}>
            <Plus size={14} />
            Add Scenario
          </button>
          {cases.map((v, i) => (
            <button
              className={"profile-row " + (i === index ? "selected" : "")}
              key={v.id}
              onClick={() => load(i)}
            >
              <strong>{v.name}</strong>
              <Badge tone={v.review_status === "reviewed" ? "teal" : "neutral"}>
                {label(v.review_status)}
              </Badge>
            </button>
          ))}
        </aside>
        <section>
          {c ? (
            <>
              <Field label="Scenario Name">
                <input
                  value={c.name}
                  onChange={(e) => patch("name", e.target.value)}
                />
              </Field>
              <Field label="Persona & Situation">
                <textarea
                  dir="auto"
                  rows={3}
                  value={c.persona || ""}
                  onChange={(e) => patch("persona", e.target.value)}
                />
              </Field>
              <Field
                label="Customer Message"
                hint="Replay sends the same message and history to every model. Each additional turn needs its own complete checkpoint."
              >
                <textarea
                  dir="auto"
                  rows={3}
                  value={
                    c.checkpoints?.[0]?.user_turn || c.user_turns?.[0] || ""
                  }
                  onChange={(e) => patch("user_turns", [e.target.value])}
                />
              </Field>
              <Field label="Expected Behavior">
                <textarea
                  dir="auto"
                  rows={3}
                  value={
                    typeof c.expected_behavior === "string"
                      ? c.expected_behavior
                      : JSON.stringify(c.expected_behavior)
                  }
                  onChange={(e) => patch("expected_behavior", e.target.value)}
                />
              </Field>
              <Field label="Critical Rules · One per Line">
                <textarea
                  dir="auto"
                  rows={3}
                  value={(c.critical_rules || []).join("\n")}
                  onChange={(e) =>
                    patch(
                      "critical_rules",
                      e.target.value.split("\n").filter(Boolean),
                    )
                  }
                />
              </Field>
              <div className="b-grid two">
                <Field
                  label="Supplied Facts · JSON"
                  hint={
                    c.frozen_rendered_prompt
                      ? "Imported facts are saved in the original prompt. To change them, edit the original persona and import it again."
                      : "Identify which facts came from the source and which you invented."
                  }
                >
                  <textarea
                    className="mono"
                    rows={5}
                    value={facts}
                    readOnly={!!c.frozen_rendered_prompt}
                    onChange={(e) => setFacts(e.target.value)}
                    onBlur={() => {
                      try {
                        patch("facts", JSON.parse(facts));
                        setError(null);
                      } catch {
                        setError("Enter facts as valid JSON, then try again.");
                      }
                    }}
                  />
                </Field>
                <Field label="Saved History · JSON">
                  <textarea
                    className="mono"
                    rows={5}
                    value={history}
                    onChange={(e) => setHistory(e.target.value)}
                    onBlur={() => {
                      try {
                        patch("history", JSON.parse(history));
                        setError(null);
                      } catch {
                        setError("Enter history as valid JSON, then try again.");
                      }
                    }}
                  />
                </Field>
              </div>
              <CheckLine
                checked={c.review_status === "reviewed"}
                onChange={(v) =>
                  patch("review_status", v ? "reviewed" : "draft")
                }
              >
                I have reviewed this scenario and its expected behavior
              </CheckLine>
            </>
          ) : (
            <p className="b-muted">
              Add a scenario or generate drafts to begin.
            </p>
          )}
        </section>
      </div>
      <div className="modal-actions">
        <span className="b-muted">
          {cases.length} scenarios · new versions preserve saved runs
        </span>
        <button
          className="b-button primary"
          disabled={busy || !form.name || !cases.length}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              let copy = cases;
              if (c)
                copy = cases.map((v, i) =>
                  i === index
                    ? {
                        ...v,
                        facts: c.frozen_rendered_prompt
                          ? v.facts
                          : JSON.parse(facts),
                        history: JSON.parse(history),
                        ...(v.checkpoints?.length
                          ? {
                              checkpoints: v.checkpoints.map(
                                (cp: Obj, j: number) =>
                                  j === 0
                                    ? { ...cp, history: JSON.parse(history) }
                                    : cp,
                              ),
                            }
                          : {}),
                      }
                    : v,
                );
              const s = await request<Suite>(
                "/suites" + (suite ? "/" + suite.id + "/versions" : ""),
                { ...form, cases: copy },
              );
              onSaved(s.id);
              onClose();
              toast.success("Scenario version saved");
            } catch (e) {
              setError(e);
            } finally {
              setBusy(false);
            }
          }}
        >
          <Save size={15} />
          Save Scenario Set
        </button>
      </div>
    </Modal>
  );
}
